import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { calculateTotalPages } from "../../../utils/Helper";
import {
  CalculatorPresetListAction,
  CalculatorPresetCreateAction,
  CalculatorPresetUpdateAction,
  CalculatorPresetDeleteAction,
} from "../../../Redux/Actions/PricingAction";
import {
  PricingPageHeader,
  StatusChip,
  pricingBodyRowSx,
  pricingTableContainerSx,
  pricingTableHeadSx,
} from "../shared/pricingHelpers";
import CalculatorPresetsForm, {
  CalculatorPresetValues,
} from "./CalculatorPresetsForm/CalculatorPresetsForm";

const ROWS_PER_PAGE = 10;

const CalculatorPresets: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { rows = [], loading } = useSelector(
    (state: any) => state.CalculatorPresetListData || {}
  );

  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchList = () => dispatch(CalculatorPresetListAction());

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paged = useMemo(
    () => rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
    [rows, page]
  );

  const openCreate = () => {
    setMode("create");
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: any) => {
    setMode("edit");
    setEditing(row);
    setFormOpen(true);
  };

  const initialFromRow = (row: any): Partial<CalculatorPresetValues> | null =>
    row
      ? {
          key: row.key ?? "",
          name: row.name ?? "",
          meta: row.meta ?? "",
          display_order: row.display_order ?? 0,
          is_active: row.is_active ?? true,
          line_items: Array.isArray(row.line_items)
            ? row.line_items.map((li: any) => ({
                feature_key: li.feature_key ?? "",
                qty: li.qty ?? 1,
              }))
            : [{ feature_key: "", qty: 1 }],
        }
      : null;

  const handleSubmit = async (payload: any) => {
    setSaving(true);
    const res =
      mode === "create"
        ? await dispatch(CalculatorPresetCreateAction(payload))
        : await dispatch(CalculatorPresetUpdateAction(editing.id, payload));
    setSaving(false);
    if (res?.status === true) {
      setFormOpen(false);
      fetchList();
    }
  };

  const handleDelete = (row: any) => {
    Swal.fire({
      title: "Delete preset?",
      text: `"${row.name}" will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d92d20",
      cancelButtonColor: "#667085",
      confirmButtonText: "Delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const res = await dispatch(CalculatorPresetDeleteAction(row.id));
        if (res?.status === true) fetchList();
      }
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Calculator Presets"
            breadcrumbs={[
              { label: "Pricing", path: "/pricing/calculator-presets", isActive: false },
              { label: "Calculator Presets", path: "/pricing/calculator-presets", isActive: true },
            ]}
          />
        </div>

        <PricingPageHeader
          title="Calculator Presets"
          subtitle="Preset carts that pre-fill the pricing calculator."
          actionLabel="Add Preset"
          onAction={openCreate}
        />

        <Box sx={{ mx: 2, mb: 2 }}>
          <TableContainer component={Paper} sx={pricingTableContainerSx}>
            <Table aria-label="Calculator Presets">
              <TableHead sx={pricingTableHeadSx}>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Line Items</TableCell>
                  <TableCell>Meta</TableCell>
                  <TableCell align="right">Order</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length > 0 ? (
                  paged.map((row: any, idx: number) => (
                    <TableRow key={row.id} sx={pricingBodyRowSx}>
                      <TableCell>{(page - 1) * ROWS_PER_PAGE + idx + 1}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>{row.key}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">
                        {Array.isArray(row.line_items) ? row.line_items.length : 0}
                      </TableCell>
                      <TableCell>{row.meta ?? "—"}</TableCell>
                      <TableCell align="right">{row.display_order}</TableCell>
                      <TableCell align="center">
                        <StatusChip active={Boolean(row.is_active)} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                            sx={{ color: "#d92d20" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#667085" }}>
                      No presets found. Click “Add Preset” to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {rows.length > ROWS_PER_PAGE && (
          <div className="pagination-style">
            <Stack spacing={2}>
              <Pagination
                count={calculateTotalPages(rows.length, ROWS_PER_PAGE)}
                page={page}
                onChange={(_e, p) => setPage(p)}
                color="primary"
                variant="outlined"
                shape="rounded"
              />
            </Stack>
          </div>
        )}
      </div>

      <CalculatorPresetsForm
        open={formOpen}
        mode={mode}
        initial={initialFromRow(editing)}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />
      <ToastDefault />
    </>
  );
};

export default CalculatorPresets;
